import { Button } from "@/components/ui/button";
import { Download, Trash2, FileText, FileImage, File } from "lucide-react";
import { useFolderTreeContext } from "./FolderTreeContext";
import type { Document } from "@/api/generated/client/models/Document";

type FolderDocument = Pick<Document, "id" | "filename" | "mimeType" | "sizeBytes" | "folderId" | "createdAt">;

interface DocRowProps {
  doc: FolderDocument;
  depth: number;
}

/** Pick a small icon based on MIME type */
function DocIcon({ mimeType }: { mimeType?: string }) {
  if (mimeType?.startsWith("image/")) return <FileImage className="h-3.5 w-3.5 text-blue-500 shrink-0" />;
  if (mimeType === "application/pdf") return <FileText className="h-3.5 w-3.5 text-red-500 shrink-0" />;
  return <File className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
}

/** Format bytes into a human-readable size */
function formatSize(bytes?: number | null): string {
  if (bytes == null || bytes === 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocRow({ doc, depth }: DocRowProps) {
  const ctx = useFolderTreeContext();
  const indent = depth * 16;
  const isDragging = ctx.draggingDocId === doc.id;
  const size = formatSize(doc.sizeBytes);

  return (
    <div
      className={`flex items-center gap-1.5 py-0.5 pr-1 rounded-md hover:bg-muted/50 group w-full min-w-0 cursor-grab active:cursor-grabbing transition-opacity ${isDragging ? "opacity-40" : ""}`}
      style={{ paddingLeft: `${indent + 8}px` }}
      draggable={ctx.canWrite}
      onDragStart={ctx.handleDragStart(doc.id)}
      onDragEnd={ctx.handleDragEnd}
    >
      <DocIcon mimeType={doc.mimeType} />
      <button type="button" className="flex-1 min-w-0 overflow-hidden text-left" onClick={() => ctx.onOpenDoc(doc)}>
        <p className="text-xs truncate leading-tight" title={doc.filename}>{doc.filename}</p>
      </button>
      {size && <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">{size}</span>}

      <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => ctx.onDownload(doc.id, doc.filename)} title="Download">
          <Download className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="icon" className="h-5 w-5 hover:text-destructive" onClick={() => ctx.canWrite && ctx.onDeleteDoc(doc.id)} disabled={ctx.isDeleting || !ctx.canWrite} title="Delete">
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
